import * as React from "react";
import { cn } from "../../lib/utils";
import { X } from "lucide-react";

/**
 * Chip component - A compact element that represents an attribute, text, or action
 * Replaces MUI Chip with a Tailwind/shadcn compatible version
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - The text content of the chip
 * @param {'small'|'medium'} props.size - Size of the chip
 * @param {'default'|'primary'|'success'|'warning'|'error'|'secondary'} props.color - Color variant
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.icon - Optional icon to display
 */
const Chip = React.forwardRef((
  { 
    label, 
    size = 'medium', 
    color = 'default', 
    onDelete, 
    className,
    icon,
    ...props 
  }, 
  ref
) => {
  const sizeClasses = {
    small: 'h-6 text-xs px-2 gap-1',
    medium: 'h-7 text-sm px-3 gap-1.5'
  };

  const colorClasses = {
    default: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
    primary: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    success: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    warning: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    error: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
    secondary: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
  };

  const deleteButtonColors = {
    default: 'hover:bg-gray-300',
    primary: 'hover:bg-blue-200',
    success: 'hover:bg-green-200',
    warning: 'hover:bg-yellow-200',
    error: 'hover:bg-red-200',
    secondary: 'hover:bg-purple-200'
  };

  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center rounded-md font-medium border transition-colors',
        sizeClasses[size],
        colorClasses[color],
        className
      )}
      {...props}
    >
      {icon && (
        <span className="flex-shrink-0">
          {React.cloneElement(icon, { 
            className: cn(
              size === 'small' ? 'h-3 w-3' : 'h-4 w-4'
            ) 
          })}
        </span>
      )}
      <span className="truncate max-w-[150px]">{label}</span>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(e);
          }}
          className={cn(
            'flex-shrink-0 rounded-full p-0.5 transition-colors',
            deleteButtonColors[color]
          )}
        >
          <X className={cn(size === 'small' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
        </button>
      )}
    </span>
  );
});

Chip.displayName = 'Chip';

export { Chip };
